import React from 'react';
import { 
  ArrowLeft, ShoppingCart, Check, Clock, Users, Briefcase, Star, Tag
} from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import PriceEstimate from './PriceEstimate';

const ServiceDetail = ({ service, onBack, onContactSales }) => {
  const { t, i18n } = useTranslation();
  const { addToCart } = useCart();

  // The API returns already translated fields based on the endpoint used
  const title = service.title_en || service.title_tr || service.title || 'Service';
  const description = service.description_en || service.description_tr || service.description || '';
  const category = service.category_en || service.category_tr || service.category || '';
  const features = service.features_en || service.features_tr || service.features || [];

  const formatPrice = (price, priceType, subscriptionPeriod) => {
    const formatted = new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0
    }).format(price);

    if (priceType === 'subscription' && subscriptionPeriod) {
      const periodText = {
        monthly: t('shop.perMonth', '/ay'),
        quarterly: t('shop.perQuarter', '/çeyrek'),
        yearly: t('shop.perYear', '/yıl')
      };
      return `${formatted}${periodText[subscriptionPeriod] || ''}`;
    }
    return formatted;
  };

  const handleAddToCart = () => {
    addToCart({
      id: service.id,
      title: title,
      price: service.price,
      image: service.image,
      category: category,
      service_type: service.service_type
    });
    toast.success(t('shop.addedToCart', 'Added to cart'));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4">
          <button
            onClick={onBack}
            className="text-gray-600 hover:text-blue-600 transition-colors flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('shop.backToServices', 'Back to Services')}
          </button>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left Column - Image */}
          <div>
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              {service.image ? (
                <img
                  src={service.image}
                  alt={title}
                  className="w-full h-[400px] object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-[400px] bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center">
                  <div className="text-center">
                    <Briefcase className="w-20 h-20 text-blue-300 mx-auto mb-4" />
                    <p className="text-blue-400 font-medium">{category || 'Service'}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Service Type Badge */}
            <div className="mt-4 flex items-center gap-3">
              {service.service_type === 'corporate' ? (
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                  <Users className="w-4 h-4" />
                  {t('shop.corporate', 'Corporate')}
                </span>
              ) : (
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                  <Star className="w-4 h-4" />
                  {t('shop.individual', 'Individual')}
                </span>
              )}
              {category && (
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm">
                  <Tag className="w-4 h-4" />
                  {category}
                </span>
              )}
            </div>
          </div>

          {/* Right Column - Details */}
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {title}
            </h1>

            {/* Price (hidden for corporate — they use Contact Sales flow) */}
            {service.service_type === 'corporate' ? (
              <div className="mb-6">
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium">
                  <Briefcase className="w-4 h-4" />
                  {t('shop.customPricing', 'Custom pricing based on your needs')}
                </span>
              </div>
            ) : (
              <div className="mb-6">
                <span className="text-4xl font-bold text-blue-600">
                  {formatPrice(service.price, service.price_type, service.subscription_period)}
                </span>
                {service.price_type === 'one-time' && (
                  <span className="text-gray-500 ml-2">{t('shop.oneTime', 'one-time')}</span>
                )}
                <div className="text-sm text-gray-500 mt-1">
                  {i18n.language === 'tr' ? '+ %20 KDV (ödeme sırasında hesaplanır)' : '+ 20% VAT (calculated at checkout)'}
                </div>
                <div className="mt-1">
                  <PriceEstimate amount={service.price} className="text-sm" />
                </div>
              </div>
            )}

            {/* Duration if available */}
            {service.duration && (
              <div className="flex items-center gap-2 text-gray-600 mb-6">
                <Clock className="w-5 h-5" />
                <span>{service.duration}</span>
              </div>
            )}

            {/* Description */}
            <div className="prose prose-lg max-w-none mb-8">
              <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                {description}
              </p>
            </div>

            {/* Features */}
            {features && features.length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t('shop.whatsIncluded', "What's Included")}
                </h3>
                <ul className="space-y-3">
                  {features.map((feature, index) => (
                    <li key={`feature-${index}-${feature.substring(0, 10)}`} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              {service.service_type === 'corporate' ? (
                <Button
                  onClick={onContactSales}
                  className="flex-1 bg-green-600 hover:bg-green-700 py-6 text-lg"
                  data-testid="contact-sales-detail-btn"
                >
                  <Briefcase className="w-5 h-5 mr-2" />
                  {t('shop.contactSales', 'Contact Sales')}
                </Button>
              ) : (
                <Button
                  onClick={handleAddToCart}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 py-6 text-lg"
                  data-testid="add-to-cart-detail-btn"
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  {t('shop.addToCart', 'Add to Cart')}
                </Button>
              )}
            </div>

            {/* Trust Indicators */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-blue-600">100%</div>
                  <div className="text-sm text-gray-500">{t('shop.satisfaction', 'Satisfaction')}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-600">24/7</div>
                  <div className="text-sm text-gray-500">{t('shop.support', 'Support')}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-600">10+</div>
                  <div className="text-sm text-gray-500">{t('shop.yearsExp', 'Years Exp.')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetail;
